import { toBoolean } from '@mnd-frontend/ts';
import { Icons } from '@mnd-frontend/ui/publish/components';
import { PURPLE_300 } from '@mnd-frontend/ui/shared/tokens';
import { capitalizeFirstLetter, getCookie } from '@mnd-frontend/utils';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { MenuDataQuery } from '../../../../generated/types';
import mndUrl, { SubDomain } from '../../../../lib/mndUrl';
import routes from '../../../../routes';
import { type Item, type MainMenuItem, MenuState } from './types';

const Initials = styled.span`
  display: grid;
  place-content: center;
  border-radius: 50%;
  background: ${PURPLE_300};
  width: 28px;
  height: 28px;
`;

const getInitials = (name?: string) => {
  if (!name || typeof name !== 'string') {
    return '';
  }
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) {
    return '';
  }
  const firstInitial = parts[0][0]?.toUpperCase() || '';
  const lastInitial = parts.length > 1 ? parts[parts.length - 1][0] || '' : '';

  return firstInitial + lastInitial;
};

const useMainMenu = (data?: MenuDataQuery): MainMenuItem[] => {
  const { t } = useTranslation();
  const pressroomId = data?.pressroom?.id;
  const isMynewsdeskAdmin = data?.me?.user?.isMynewsdeskAdmin;
  const monitorEnabled = data?.pressroom?.features.monitor.enabled;
  const pressroomHasSearchProfiles = data?.pressroom?.monitor?.hasSearchProfiles;
  const sourceHasSmartSearchProfiles = data?.pressroom?.monitor?.hasSmartSearchProfiles;
  const showPitch = data?.pressroom?.features.pitch;
  const newAudienceBuilderAccess = data?.pressroom?.features.mndReach;
  const showAudienceBuilder =
    data?.pressroom?.features.audienceBuilder2.enabled ||
    data?.pressroom?.features.audienceBuilder2.showUpsellPage ||
    newAudienceBuilderAccess;
  const viewAsAdmin = Boolean(data?.me?.viewAsAdmin);
  const version = getCookie('frontend_version');
  const locale = getCookie('locale');
  const hasCookies = Boolean(locale || version || getCookie('feature_toggles'));

  const monitor: MainMenuItem = pressroomId
    ? {
        heading: 'Monitor',
        icon: <Icons.MonitorNavigation $size="xs" />,
        iconActive: <Icons.MonitorNavigationActive $size="xs" />,
        menuState: MenuState.Monitor,
        items:
          monitorEnabled && (pressroomHasSearchProfiles || sourceHasSmartSearchProfiles)
            ? [
                {
                  url: routes.monitorInsights({ pressroomId }),
                  text: t('monitor_navigation_insights'),
                  description: t('navigation_mention_desc_mentions'),
                  activeUrl: `${routes.monitorInsights.pattern}/*`,
                  icon: <Icons.Binoculars $size="xs" />,
                },
                {
                  url: routes.monitorSearchProfiles({ pressroomId }),
                  text: t('menu_monitor_search_profiles'),
                  description: t('navigation_mention_desc_search_profiles'),
                  activeUrl: `${routes.monitorSearchProfiles.pattern}/*`,
                  icon: <Icons.Search $size="xs" />,
                },
                {
                  url: routes.monitorAlertsList({ pressroomId }),
                  text: t('monitor_alerts_news_alerts_and_scheduling_title'),
                  description: t('monitor_alerts_news_alerts_and_scheduling_description'),
                  activeUrl: `${routes.monitorAlertsList.pattern}/*`,
                  icon: <Icons.Email $size="xs" />,
                },
                {
                  url: routes.monitorClipboardList({ pressroomId }),
                  text: t('menu_monitor_clipbooks'),
                  description: t('navigation_mention_desc_clipboards'),
                  activeUrl: `${routes.monitorClipboardList.pattern}/*`,
                  icon: <Icons.Story $size="xs" />,
                },
              ].filter(toBoolean)
            : [{ url: routes.monitor({ pressroomId }), text: 'Monitor' }],
      }
    : {
        heading: 'Monitor',
        items: [],
        menuState: MenuState.Monitor,
      };

  const getSettingsMenuItems = (data: MenuDataQuery | undefined) => {
    const items: Item[] = [];

    if (!pressroomId) return items;
    if (data?.pressroom?.newsroomSettingsPage)
      items.push({
        url: routes.newsroomSettings({ pressroomId: pressroomId as string }),
        text: t('menu_settings_newsroom_settings'),
        activeUrl: routes.newsroomSettings.pattern,
      });
    items.push({
      url: routes.publishContactPeopleList({ pressroomId: pressroomId as string }),
      text: t('menu_settings_press_contacts'),
    });
    items.push({
      url: routes.channels({ pressroomId: pressroomId as string }),
      text: t('menu_settings_channels'),
    });
    if (data?.pressroom?.features.emailReportsPage) {
      items.push({
        url: routes.emailReports({ pressroomId: pressroomId as string }),
        text: t('menu_settings_email_reports'),
      });
    }
    if (data?.pressroom?.features.tagManagement.enabled) {
      items.push({
        url: routes.tagManagement({ pressroomId: pressroomId as string }),
        text: t('menu_settings_category_management'),
      });
    }
    if (data?.pressroom?.features.copilotFeatures) {
      items.push({
        url: routes.copilotSettings({ pressroomId: pressroomId as string }),
        text: t('menu_copilot_settings_heading'),
      });
    }
    items.push({
      url: routes.emailSettings({
        pressroomId: pressroomId as string,
      }),
      activeUrl: routes.emailSettings.pattern,
      text: t('menu_additional_email'),
    });
    if (data?.pressroom?.features.storyWidget.enabled) {
      items.push({
        url: routes.storyWidget({
          pressroomId: pressroomId as string,
        }),
        activeUrl: routes.storyWidget.pattern,
        text: t('menu_additional_story_widget'),
      });
    }
    if (data?.source?.features.securityPage) {
      items.push({
        url: routes.accountSecurity({ pressroomId: pressroomId as string }),
        text: t('menu_account_security'),
      });
    }
    if (data?.source?.features.userManagementPage) {
      items.push({
        url: routes.accountUserManagement({
          pressroomId: pressroomId as string,
        }),
        text: t('menu_account_users'),
      });
    }
    items.push({
      url: routes.accountSettings({ pressroomId: pressroomId as string }),
      text: t('menu_account_membership'),
    });

    return items.filter(toBoolean);
  };

  const getUserMenuItems = (data: MenuDataQuery | undefined) => {
    const items: Item[] = [
      {
        url: pressroomId
          ? routes.userSettingsWithPressroomRoot({ pressroomId })
          : routes.userSettingsRoot(),
        text: t('menu_user_settings'),
      },
      ...((data?.me?.user?.sources.length ?? 0) > 1 || data?.me?.user?.isMynewsdeskAdmin === true
        ? [{ url: routes.pressroomSelector(), text: t('menu_user_switch_source') }]
        : []),
    ];

    !!data?.pressroom?.newsroomUrl &&
      items.push({
        url: data.pressroom.newsroomUrl,
        text: t('menu_user_newsroom'),
        newTab: true,
      });
    data?.pressroom?.features.mention.enabled &&
      items.push({
        url: mndUrl(SubDomain.Publish, `/${data.pressroom.id}/mention`),
        newTab: true,
        text: t('menu_user_mention'),
      });
    data?.pressroom?.features.eqs &&
      items.push({
        url: 'https://cockpit.eqs.com/ssl_nordic.html',
        newTab: true,
        text: t('menu_user_eqs'),
      });
    items.push({
      url: mndUrl(SubDomain.WWW, `/${data?.pressroom?.siteCode ?? ''}/stories`),
      newTab: true,
      text: t('journalist.menu.stories'),
    });
    items.push({
      url: mndUrl(SubDomain.Publish, '/user/signout'),
      text: t('menu_user_logout'),
    });
    if (isMynewsdeskAdmin) {
      items.push({ separator: true, url: mndUrl(SubDomain.WWW, '/admin'), text: 'Go to admin' });
    }
    return items.filter(toBoolean);
  };

  if (data?.me?.user?.isOnlyJournalist) {
    return [];
  }

  const reportUpsellPage =
    data?.source?.features.brandReport?.enabled === false &&
    data?.source?.features.coverageReports.enabled === false &&
    data?.source?.features.brandReport.previousTrial === false;

  const adminIcon = hasCookies ? (
    <Icons.AdminShieldCookie $size="xs" $color={viewAsAdmin ? 'RED_700' : 'GREY_900'} />
  ) : (
    <Icons.AdminShield $size="xs" $color={viewAsAdmin ? 'RED_700' : 'GREY_900'} />
  );

  const mainMenu: (MainMenuItem | null)[] = [
    {
      heading: 'Dashboard',
      icon: <Icons.Dashboard $size="xs" />,
      iconActive: <Icons.DashboardActive $size="xs" />,
      menuState: MenuState.Dashboard,
      items: pressroomId
        ? [
            {
              url: routes.dashboard({ pressroomId }),
              text: 'Dashboard',
            },
          ]
        : [],
    },
    {
      heading: 'Publish',
      icon: <Icons.Pen $size="xs" />,
      iconActive: <Icons.PublishActive $size="xs" />,
      menuState: MenuState.Publish,
      items:
        data?.pressroom && data?.source && pressroomId
          ? [
              {
                url: routes.publish({ pressroomId }),
                activeUrl: [
                  routes.publish.pattern,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'pressreleases',
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'news',
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'blog_posts',
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'events',
                  })}/*`,
                ],
                text: capitalizeFirstLetter(t('material_name_collection')),
                description: t('navigation_description_publications'),
                icon: <Icons.Story $size="xs" />,
              },
              showPitch && {
                url: routes.pitchList({ pressroomId }),
                activeUrl: routes.pitchList.pattern,
                text: 'Pitch',
                icon: <Icons.Plane $size="xs" />,
                description: t('navigation_description_pitch'),
              },
              {
                url: routes.publishList({
                  pressroomId,
                  materialType: data.pressroom.features.images ? 'images' : 'videos',
                }),
                activeUrl: [
                  `${routes.publishImagesList({
                    pressroomId,
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'videos',
                  })}/*`,
                  `${routes.publishList({
                    pressroomId,
                    materialType: 'documents',
                  })}/*`,
                ],
                text: t('common_media'),
                icon: <Icons.Image $size="xs" />,
                description: t('navigation_description_media'),
              },
            ].filter(toBoolean)
          : [],
    },
    {
      heading: 'Contacts',
      icon: <Icons.ContactsNavigation $size="xs" />,
      iconActive: <Icons.ContactsNavigationActive $size="xs" />,
      menuState: MenuState.Contacts,
      items:
        data?.pressroom && pressroomId
          ? [
              {
                url: routes.contacts({ pressroomId }),
                activeUrl: [
                  routes.contacts.pattern,
                  routes.contactListShow.pattern,
                  routes.contactsAll.pattern,
                  routes.contactsInactive.pattern,
                  routes.contactShow.pattern,
                ],
                exactMatch: true,
                text: t('menu_contacts_contact_lists'),
                icon: <Icons.List $size="xs" />,
                description: t('navigation_contacts_desc_contact_lists'),
              },
              showAudienceBuilder && {
                url: newAudienceBuilderAccess
                  ? routes.contactAudienceBuilderMndReach({ pressroomId })
                  : routes.contactAudienceBuilder({ pressroomId, tab: 'advanced' }),
                activeUrl: [
                  routes.contactAudienceBuilderMndReach.pattern,
                  routes.contactAudienceBuilder.pattern,
                  '/:pressroomId/unlocker/audience_builder',
                ],
                ...(newAudienceBuilderAccess
                  ? {
                      badge: {
                        text: t('common_new'),
                        variant: 'commercial' as const,
                      },
                    }
                  : {}),
                text: t('menu_contacts_audience_builder'),
                icon: <Icons.Binoculars $size="xs" />,
                description: t('navigation_contacts_desc_audience_builder'),
              },
              {
                url: routes.contactImport({ pressroomId }),
                text: t('menu_contacts_import'),
                activeUrl: [routes.contactImport.pattern, routes.contactImportTab.pattern],
                icon: <Icons.Upload $size="xs" />,
                description: t('navigation_contacts_desc_import'),
              },
            ].filter(toBoolean)
          : [],
    },
    {
      heading: 'Analyze',
      icon: <Icons.Report $size="xs" />,
      iconActive: <Icons.AnalyzeNavigationActive $size="xs" />,
      menuState: MenuState.Analyze,
      items:
        data?.pressroom && pressroomId
          ? data?.pressroom?.features.analyzeLight.enabled ||
            data?.pressroom?.features.analyze.enabled
            ? [
                data?.pressroom?.features.analyzeLight.enabled && {
                  url: routes.analyzeOverview({
                    pressroomId,
                  }),
                  text: t('menu_analyze_reports_and_insights'),
                  description: t('navigation_analyze_desc_overview'),
                  icon: <Icons.Analyze $size="xs" />,
                },
                data?.pressroom?.features.analyze.enabled && {
                  url: routes.analyzeNewsroom({
                    pressroomId,
                  }),
                  activeUrl: routes.analyzeNewsroom.pattern,
                  exactMatch: true,
                  text: t('menu_analyze_newsroom'),
                  description: t('navigation_analyze_desc_newsroom'),
                  icon: <Icons.Newsroom $size="xs" />,
                },
                data?.pressroom?.features.analyze.enabled && {
                  url: routes.analyzeMaterialList({
                    pressroomId,
                  }),
                  activeUrl: `${routes.analyzeMaterialList({
                    pressroomId,
                  })}/*`,
                  text: t('menu_analyze_materials'),
                  description: t('navigation_analyze_desc_your_content'),
                  icon: <Icons.Story $size="xs" />,
                },
              ].filter(toBoolean)
            : [
                {
                  text: 'unlocker',
                  url: routes.analyzeNewsroom({ pressroomId }), // changed from "routes.analyze" since "/:pressroomId/analyze" is redirected to "/:pressroomId/analyze/newsroom"
                },
              ]
          : [],
    },
    monitor,
    {
      heading: 'Reports',
      icon: <Icons.Lightbulb $size="xs" />,
      iconActive: <Icons.ReportsActive $size="xs" />,
      menuState: MenuState.Reports,
      items:
        data?.pressroom && pressroomId
          ? reportUpsellPage
            ? [
                {
                  url: routes.reportsUpsell({ pressroomId }),
                  text: 'Reports',
                },
              ]
            : [
                {
                  url: routes.reports({ pressroomId }),
                  activeUrl: [
                    routes.reports.pattern,
                    routes.reportsEdit.pattern,
                    routes.reportsSlideshow.pattern,
                    routes.reportsPreview.pattern,
                    routes.reportsCreateBrand.pattern,
                    routes.reportsPreviewSection.pattern,
                  ],
                  exactMatch: true,
                  text: 'Reports',
                },
              ]
          : [],
    },
    {
      heading: t('menu_help'),
      icon: <Icons.CircleQuestion $size="xs" />,
      menuState: MenuState.Help,
      items: [
        {
          url: routes.external.help(data?.me?.user.locale),
          newTab: true,
          text: t('menu_user_support'),
        },
        {
          url: routes.external.library(data?.me?.user.locale),
          newTab: true,
          text: t('menu_user_library'),
        },
      ],
    },
    {
      heading: t('common_settings'),
      icon: <Icons.Settings $size="xs" />,
      menuState: MenuState.Settings,
      items: getSettingsMenuItems(data),
    },
    {
      heading: data?.me?.user?.name || data?.me?.user?.username || 'User',
      icon: isMynewsdeskAdmin ? adminIcon : <Icons.User $size="xs" />,
      desktopIcon: isMynewsdeskAdmin ? (
        adminIcon
      ) : (
        <Initials>
          {getInitials(data?.me?.user?.name || data?.me?.user?.username || 'User')}
        </Initials>
      ),
      menuState: MenuState.User,
      addDebugInfo: isMynewsdeskAdmin,
      items: getUserMenuItems(data),
    },
  ];

  return mainMenu.filter(toBoolean).filter(menuGroup => menuGroup.items.length > 0);
};

export default useMainMenu;
